import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, timeAgo } from "@/api";
import { toast } from "sonner";
import {
  Broadcast,
  ArrowsClockwise,
  CheckCircle,
  XCircle,
  Waves,
  Fire,
  ChartBar,
  Lightning,
} from "@phosphor-icons/react";

// Renders a signed alpha bar in [-1, +1] range with color coding.
function AlphaBar({ alpha }) {
  if (alpha == null) return <span className="text-white/25 font-mono text-[10px]">-</span>;
  const pct = Math.min(100, Math.abs(alpha) * 100);
  const color = alpha > 0.15 ? "#00FF66" : alpha > 0 ? "#8DD35B" : alpha < -0.15 ? "#FF3B30" : "#F5C518";
  return (
    <div className="relative h-2 w-16 bg-white/5 border border-white/10">
      <div
        className="absolute top-0 h-full"
        style={{
          width: `${pct}%`,
          background: color,
          left: alpha >= 0 ? "50%" : `${50 - pct / 2}%`,
        }}
      />
      <div className="absolute top-0 bottom-0 left-1/2 w-px bg-white/40" />
    </div>
  );
}

const ICONS = {
  cm_trend: <ChartBar size={12} className="text-[#00FF66]" />,
  cm_volume_flow: <Waves size={12} className="text-[#007AFF]" />,
  cm_rapid_movements: <Fire size={12} className="text-[#F5C518]" />,
  cm_liquidations: <Lightning size={12} className="text-[#FF3B30]" />,
  cm_ls_ratio: <ChartBar size={12} className="text-[#8DD35B]" />,
  cm_long_shorts: <ChartBar size={12} className="text-[#F5C518]" />,
  cm_pair_volume: <Waves size={12} className="text-[#00FF66]" />,
};

export function CryptometerPanel() {
  const qc = useQueryClient();

  const snap = useQuery({
    queryKey: ["cryptometer-snapshot"],
    queryFn: api.cryptometerSnapshot,
    refetchInterval: 60_000,
  });

  const weights = useQuery({
    queryKey: ["cryptometer-weights"],
    queryFn: api.cryptometerWeights,
    refetchInterval: 60_000,
  });

  const refresh = useMutation({
    mutationFn: api.cryptometerRefresh,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cryptometer-snapshot"] });
      toast.success("Cryptometer refreshed");
    },
    onError: () => toast.error("Refresh failed — check API key"),
  });

  const toggleSource = useMutation({
    mutationFn: ({ id, enabled }) => api.cryptometerToggleSource(id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cryptometer-weights"] }),
  });

  const reset = useMutation({
    mutationFn: api.cryptometerResetWeights,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cryptometer-weights"] });
      toast.info("Cryptometer weights reset to defaults");
    },
  });

  const trend = snap.data?.trend;
  const vflow = snap.data?.volume_flow;
  const rapid = snap.data?.rapid_movements || [];
  const info = snap.data?.info;
  const usage = snap.data?.usage;
  const sources = weights.data?.sources || [];
  const wmap = weights.data?.weights || {};
  const enabledMap = weights.data?.enabled || {};
  const edges = weights.data?.edges || {};

  const configured = usage?.api_key_configured;

  return (
    <div
      className="border border-white/10 bg-[#0d0d0d]"
      data-testid="cryptometer-panel"
    >
      <div className="px-3 py-2 border-b border-white/10 flex items-center gap-3 flex-wrap">
        <Broadcast size={14} className="text-[#00FF66]" weight="fill" />
        <span className="font-display text-sm tracking-widest uppercase text-white">
          Cryptometer Alpha
        </span>
        <span className="font-mono text-[10px] text-white/40">
          Global + per-pair market intelligence · auto-tuned weights
        </span>
        <div className="ml-auto flex items-center gap-2">
          {info && (
            <span className="font-mono text-[10px] text-white/50" title="Cryptometer API plan">
              plan: <span className="text-white/70">{info.plan}</span>
              {" · "}used: <span className="text-white/70">{info.total_requests || 0}</span>
            </span>
          )}
          {snap.data?.updated_at && (
            <span className="font-mono text-[10px] text-white/40">
              {timeAgo(snap.data.updated_at)}
            </span>
          )}
          <button
            data-testid="cryptometer-refresh-btn"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending || !configured}
            className="px-3 py-1 border border-[#00FF66]/50 hover:bg-[#00FF66]/10 text-[#00FF66] font-mono text-[10px] uppercase tracking-widest disabled:opacity-40 flex items-center gap-1"
          >
            <ArrowsClockwise size={10} weight="bold" className={refresh.isPending ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            data-testid="cryptometer-reset-btn"
            onClick={() => {
              if (confirm("Reset all Cryptometer weights to 1.0?")) reset.mutate();
            }}
            className="px-2 py-1 border border-white/10 hover:bg-white/5 text-white/50 font-mono text-[10px] uppercase tracking-widest"
          >
            Reset
          </button>
        </div>
      </div>

      {!configured && (
        <div className="p-4 text-center text-white/50 font-mono text-[11px]">
          CRYPTOMETER_API_KEY not configured on the backend. Add it to{" "}
          <code className="text-[#F5C518]">/app/backend/.env</code> to enable alpha ingestion.
        </div>
      )}

      {configured && (
        <>
          {/* Global row: Trend + Rapid Movements + Volume Flow summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-b border-white/10">
            {/* Trend Indicator */}
            <div className="p-3 border-r border-white/10" data-testid="cm-trend-card">
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">
                Trend Indicator V3
              </div>
              {trend ? (
                <>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-2xl text-white">
                      {trend.trend_score ? Number(trend.trend_score).toFixed(0) : "-"}
                    </span>
                    <span className="font-mono text-[10px] text-white/40">trend score</span>
                  </div>
                  <div className="mt-2 space-y-0.5 font-mono text-[10px]">
                    <div className="flex justify-between">
                      <span className="text-[#00FF66]">Buy pressure</span>
                      <span className="text-white">{Number(trend.buy_pressure || 0).toFixed(2)}%</span>
                    </div>
                    <div className="h-1 bg-white/5 relative">
                      <div
                        className="absolute h-full bg-[#00FF66]"
                        style={{ width: `${Math.min(100, trend.buy_pressure || 0)}%` }}
                      />
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#FF3B30]">Sell pressure</span>
                      <span className="text-white">{Number(trend.sell_pressure || 0).toFixed(2)}%</span>
                    </div>
                    <div className="h-1 bg-white/5 relative">
                      <div
                        className="absolute h-full bg-[#FF3B30]"
                        style={{ width: `${Math.min(100, trend.sell_pressure || 0)}%` }}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="font-mono text-[10px] text-white/30">No data</div>
              )}
            </div>

            {/* Rapid Movements */}
            <div className="p-3 border-r border-white/10" data-testid="cm-rapid-card">
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">
                Rapid Movements (last 4h)
              </div>
              {rapid.length ? (
                <>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-2xl text-white">{rapid.length}</span>
                    <span className="font-mono text-[10px] text-white/40">
                      pumps + dumps
                    </span>
                  </div>
                  <div className="mt-2 max-h-24 overflow-auto font-mono text-[10px] space-y-0.5">
                    {rapid.slice(0, 6).map((r, i) => (
                      <div
                        key={i}
                        className="flex justify-between border-b border-white/5 py-0.5"
                      >
                        <span className="text-white/70">{r.pair}</span>
                        <span style={{ color: r.side === "PUMP" ? "#00FF66" : "#FF3B30" }}>
                          {r.side} {Number(r.change_detected || 0).toFixed(2)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="font-mono text-[10px] text-white/30">Nothing detected</div>
              )}
            </div>

            {/* Volume Flow */}
            <div className="p-3" data-testid="cm-vflow-card">
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">
                Volume Flow (4h)
                {(!vflow || (!vflow.inflow?.length && !vflow.outflow?.length)) && (
                  <span className="ml-2 text-[#F5C518]">PAID</span>
                )}
              </div>
              {vflow && (vflow.inflow?.length || vflow.outflow?.length) ? (
                <div className="font-mono text-[10px] space-y-0.5">
                  <div className="text-[#00FF66]">Top inflows</div>
                  {(vflow.inflow || []).slice(0, 3).map((f, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-white/60">
                        {f.from} → {f.to}
                      </span>
                      <span className="text-white/80">
                        ${(Number(f.volume || 0) / 1e6).toFixed(2)}M
                      </span>
                    </div>
                  ))}
                  <div className="text-[#FF3B30] pt-1">Top outflows</div>
                  {(vflow.outflow || []).slice(0, 3).map((f, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-white/60">
                        {f.from} → {f.to}
                      </span>
                      <span className="text-white/80">
                        ${(Number(f.volume || 0) / 1e6).toFixed(2)}M
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="font-mono text-[10px] text-white/30">
                  Requires paid Cryptometer plan
                </div>
              )}
            </div>
          </div>

          {/* Sources table with weights, edges, and enable toggle */}
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full font-mono text-[11px]" data-testid="cm-sources-table">
              <thead className="sticky top-0 bg-[#0d0d0d] border-b border-white/10">
                <tr className="text-left text-white/40 uppercase tracking-wider">
                  <th className="px-3 py-1.5 font-normal">Source</th>
                  <th className="px-2 py-1.5 font-normal text-right">Weight</th>
                  <th className="px-2 py-1.5 font-normal text-right">Edge</th>
                  <th className="px-2 py-1.5 font-normal text-right">Samples</th>
                  <th className="px-2 py-1.5 font-normal text-center">On</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((s) => {
                  const w = wmap[s.id] || 1;
                  const enabled = enabledMap[s.id] !== false;
                  const edge = edges[s.id];
                  const edgeColor = edge?.edge >= 15 ? "#00FF66" : edge?.edge <= -10 ? "#FF3B30" : "#F5C518";
                  return (
                    <tr
                      key={s.id}
                      className={`border-b border-white/5 ${enabled ? "" : "opacity-40"}`}
                      data-testid={`cm-source-${s.id}`}
                    >
                      <td className="px-3 py-1.5 text-white/80">
                        <div className="flex items-center gap-2">
                          {ICONS[s.id]}
                          <span>{s.description}</span>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <span
                          style={{
                            color: w >= 1.4 ? "#00FF66" : w <= 0.5 ? "#FF3B30" : "#fff",
                          }}
                        >
                          {w.toFixed(2)}×
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {edge && edge.total_samples > 0 ? (
                          <span style={{ color: edgeColor }}>
                            {edge.edge > 0 ? "+" : ""}
                            {edge.edge.toFixed(1)}pp
                          </span>
                        ) : (
                          <span className="text-white/25">-</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right text-white/60">
                        {edge?.total_samples ? edge.total_samples : "-"}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          data-testid={`cm-toggle-${s.id}`}
                          onClick={() => toggleSource.mutate({ id: s.id, enabled: !enabled })}
                          className="inline-flex"
                          title={enabled ? "Disable" : "Enable"}
                        >
                          {enabled ? (
                            <CheckCircle size={14} weight="fill" className="text-[#00FF66]" />
                          ) : (
                            <XCircle size={14} className="text-white/30" />
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-3 py-1.5 border-t border-white/10 flex flex-wrap gap-3 text-[10px] font-mono text-white/40">
            <span>
              Auto-tuner runs every hour. Bumps weight ×1.10 on edge ≥ +15pp (20+ samples), cuts ×0.70 on edge ≤ -10pp.
            </span>
            {weights.data?.last_tuned_at && (
              <span className="ml-auto">last tune: {timeAgo(weights.data.last_tuned_at)}</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Placeholder to satisfy AlphaBar import unused-warning if unused elsewhere.
export { AlphaBar };
